import React, { useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, Input, Modal, message, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, FilterOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import CommonTable from 'components/CommonTable';
import { getHVAList, deactivateHVA, reactivateHVA, IHVAItem } from 'api/operation/group';
import s from '../s.module.less';

const STATUS_OPTIONS = [
    { value: 'NORMAL', label: 'Active' },
    { value: 'PROVIDER_LOGIN_NOT', label: 'Inactive' },
];

const LOGIN_STATUS_DISPLAY: Record<string, { cls: string; label: string }> = {
    NORMAL: { cls: s.active, label: 'Active' },
    PROVIDER_LOGIN_NOT: { cls: s.inactive, label: 'Inactive' },
};

const HVATable = () => {
    const [pendingNameFilter, setPendingNameFilter] = useState('');
    const [nameFilter, setNameFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState<string[]>([]);
    const [current, setCurrent] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<IHVAItem[]>([]);
    const [total, setTotal] = useState(0);
    const [confirmModal, setConfirmModal] = useState<{ action: 'Deactivate' | 'Reactivate'; member: IHVAItem } | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getHVAList({
                current,
                size: pageSize,
                name: nameFilter || undefined,
                loginStatuses: statusFilter.length > 0 ? statusFilter : undefined,
            });
            if (res && !res.error) {
                setData(res.data?.data?.records || []);
                setTotal(res.data?.data?.total || 0);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [current, pageSize, nameFilter, statusFilter]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleConfirm = useCallback(async () => {
        if (!confirmModal) return;
        setActionLoading(true);
        try {
            const { action, member } = confirmModal;
            const res = action === 'Deactivate'
                ? await deactivateHVA(member.userId)
                : await reactivateHVA(member.userId);
            if (res && !res.error) {
                message.success(
                    action === 'Deactivate'
                        ? `${member.name} has been deactivated.`
                        : `${member.name} has been reactivated.`,
                );
                setConfirmModal(null);
                fetchData();
            } else {
                message.error(res?.error || `Failed to ${action.toLowerCase()}`);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setActionLoading(false);
        }
    }, [confirmModal, fetchData]);

    const columns: ColumnsType<IHVAItem> = [
        {
            title: 'NAME',
            dataIndex: 'name',
            width: '280px',
            fixed: 'left',
            className: s.tableColumn,
            filterIcon: <SearchOutlined />,
            filterDropdown: ({ close }) => (
                <div style={{ padding: 16 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        placeholder="Search name"
                        value={pendingNameFilter}
                        onChange={(e) => setPendingNameFilter(e.target.value)}
                        onPressEnter={() => {
                            setNameFilter(pendingNameFilter);
                            setCurrent(1);
                            close();
                        }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setNameFilter(pendingNameFilter);
                                setCurrent(1);
                                close();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPendingNameFilter('');
                                setNameFilter('');
                                setCurrent(1);
                                close();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            render: (_: string, row: IHVAItem) => (
                <div className={s.nameWrap}>
                    <div className={s.name}>
                        <a
                            className={s.gpLink}
                            onClick={() => window.open(`/operation/group-practice/staff-detail/${row.userId}`, '_blank')}
                        >
                            {row.name}
                        </a>
                        <div className={s.email}>{row.email}</div>
                    </div>
                </div>
            ),
        },
        {
            title: 'PRACTICES',
            dataIndex: 'practicesCount',
            width: '120px',
            className: s.tableColumn,
        },
        {
            title: 'STATUS',
            dataIndex: 'loginStatus',
            width: '140px',
            className: s.tableColumn,
            filterIcon: <FilterOutlined style={{ color: statusFilter.length > 0 ? '#F6891F' : undefined }} />,
            filterDropdown: ({ confirm }) => (
                <div className={s.filterDropdown} onKeyDown={(e) => e.stopPropagation()}>
                    <Checkbox.Group
                        value={statusFilter}
                        onChange={(vals) => {
                            setStatusFilter(vals as string[]);
                            setCurrent(1);
                            confirm({ closeDropdown: true });
                        }}
                    >
                        <div className={s.filterCheckbox}>
                            {STATUS_OPTIONS.map((opt) => (
                                <Checkbox key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Checkbox>
                            ))}
                        </div>
                    </Checkbox.Group>
                </div>
            ),
            render: (loginStatus: string) => {
                const item = LOGIN_STATUS_DISPLAY[loginStatus];
                if (!item) return loginStatus;
                return <span className={`${s.statusBadge} ${item.cls}`}>{item.label}</span>;
            },
        },
        {
            title: 'LAST ACTIVE',
            dataIndex: 'lastActiveAt',
            width: '160px',
            className: s.tableColumn,
            render: (v: string | null) => (v ? dayjs(v).format('MM/DD/YYYY') : '—'),
        },
        {
            title: 'ACTION',
            width: '200px',
            fixed: 'right',
            className: s.tableColumn,
            render: (_: unknown, row: IHVAItem) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                    {row.loginStatus !== 'PROVIDER_LOGIN_NOT' && (
                        <span
                            className={s.actionRelist}
                            style={{ color: '#EF4444' }}
                            onClick={() => setConfirmModal({ action: 'Deactivate', member: row })}
                        >
                            Deactivate
                        </span>
                    )}
                    {row.loginStatus === 'PROVIDER_LOGIN_NOT' && (
                        <span
                            className={s.actionRelist}
                            onClick={() => setConfirmModal({ action: 'Reactivate', member: row })}
                        >
                            Reactivate
                        </span>
                    )}
                    <span
                        className={s.actionView}
                        onClick={() => window.open(`/operation/group-practice/staff-detail/${row.userId}`, '_blank')}
                    >
                        View
                    </span>
                </div>
            ),
        },
    ];

    return (
        <>
            <CommonTable<IHVAItem>
                rowKey="userId"
                bordered
                loading={loading}
                columns={columns}
                data={data}
                pagination={{
                    current,
                    pageSize,
                    total,
                }}
                onChange={(pagination) => {
                    setCurrent(pagination?.current || 1);
                    setPageSize(pagination?.pageSize || 10);
                }}
                scroll={{ x: '900' }}
            />

            <Modal
                open={!!confirmModal}
                onCancel={() => setConfirmModal(null)}
                title={confirmModal?.action === 'Deactivate' ? 'Deactivate team member' : 'Reactivate team member'}
                closeIcon={<CloseOutlined />}
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                        <Button onClick={() => setConfirmModal(null)}>Cancel</Button>
                        <Button
                            type="primary"
                            danger={confirmModal?.action === 'Deactivate'}
                            loading={actionLoading}
                            onClick={handleConfirm}
                        >
                            {confirmModal?.action === 'Deactivate' ? 'Deactivate' : 'Reactivate'}
                        </Button>
                    </div>
                }
                destroyOnClose
            >
                {confirmModal?.action === 'Deactivate' ? (
                    <p style={{ fontSize: 14, color: '#4B5563' }}>
                        Are you sure you want to deactivate <strong>{confirmModal?.member.name}</strong>? They will lose
                        access to all {confirmModal?.member.practicesCount} managed practices.
                    </p>
                ) : (
                    <p style={{ fontSize: 14, color: '#4B5563' }}>
                        Reactivate <strong>{confirmModal?.member.name}</strong>? They will regain access to their managed
                        practices.
                    </p>
                )}
            </Modal>
        </>
    );
};

export default HVATable;
